/**
 * Ingress sources API client.
 *
 * Mirrors `backend/src/hub/routes/ingress.py`. One row per
 * configured inbound source — the ``type`` field discriminates which
 * adapter handles the source and which ``config`` JSON Schema applies.
 */
import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

export type IngressType =
  | "webhook"
  | "sftp_pull"
  | "file_watch"
  | "db_poll"
  | "http_cron_pull";

/**
 * Declared payload format for an IngressSource. Built-in types in v1:
 *   - "auto"   : sniff from Content-Type / file extension (default)
 *   - "json"   : parse as JSON
 *   - "xml"    : parse XML into a {tag: {…}} dict
 *   - "csv"    : parse with delimiter/header/encoding from config
 *   - "txt"    : decode bytes as text
 *   - "binary" : pass raw bytes through (workflow must accept bytes)
 */
export interface FormatSpec {
  type: string;
  config?: Record<string, unknown> | null;
}

export interface IngressSource {
  id: string;
  type: IngressType;
  name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  format: FormatSpec;
  cursor: Record<string, unknown> | null;
  secret_refs: string[];
  description: string | null;
  /** Free-form organizational tags. Always a list. */
  tags: string[];
  last_poll_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface IngressCreate {
  type: IngressType;
  name: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
  format?: FormatSpec | null;
  secret_refs?: string[];
  description?: string | null;
  tags?: string[];
}

export interface IngressUpdate {
  enabled?: boolean;
  config?: Record<string, unknown>;
  format?: FormatSpec | null;
  secret_refs?: string[];
  description?: string | null;
  /** ``undefined`` = leave unchanged; ``[]`` = clear all tags. */
  tags?: string[];
}

export interface AdapterTypeInfo {
  type: IngressType;
  mode: "push" | "pull";
  /** Pydantic-generated JSON Schema for the adapter's ``config``. */
  config_schema: {
    properties?: Record<string, Record<string, unknown>>;
    required?: string[];
    [k: string]: unknown;
  };
  /** True when format is fixed (e.g. db_poll → json) and the UI should
   *  hide the format selector. */
  format_forced: boolean;
  /** Format types this adapter can use (often the same as the registry). */
  supported_formats: string[];
}

export interface WorkflowConsumerSummary {
  id: string;
  name: string;
  direction: string;
  active_version_id: string | null;
  active_version_number: number | null;
  enabled: boolean;
}

export interface TestResult {
  ok: boolean;
  result?: Record<string, unknown>;
  error?: string;
}

export const ingressApi = {
  list: (params?: {
    page?: number;
    page_size?: PageSize;
    type?: string;
    enabled?: boolean;
    tag?: string[];
  }) => api.get<Paginated<IngressSource>>("/ingress", { query: params }),

  get: (id: string) => api.get<IngressSource>(`/ingress/${id}`),

  create: (body: IngressCreate) => api.post<IngressSource>("/ingress", body),

  update: (id: string, body: IngressUpdate) =>
    api.patch<IngressSource>(`/ingress/${id}`, body),

  remove: (id: string) => api.delete<void>(`/ingress/${id}`),

  pause: (id: string) => api.post<IngressSource>(`/ingress/${id}/pause`),

  resume: (id: string) => api.post<IngressSource>(`/ingress/${id}/resume`),

  test: (id: string) => api.post<TestResult>(`/ingress/${id}/test`),

  types: () => api.get<AdapterTypeInfo[]>("/ingress/types"),

  consumers: (id: string) =>
    api.get<WorkflowConsumerSummary[]>(`/ingress/${id}/consumers`),
};
